import { TFunction } from 'i18next';
// eslint-disable-next-line no-restricted-imports
import moment from 'moment';
import { ColorString } from './colors';
import { DefaultDateFormat } from './dates';
import { InvoiceV2 } from './invoices';

export enum USER_STATUS {
  DRAFT = 'draft',
  SCHEDULED = 'scheduled',
  INVOICE_SENT = 'invoiceSent',
  NOTIFICATION_SENT = 'notificationSent',
  PAST_DUE = 'pastDue',
  PROCESSING = 'processing',
  CHARGE_FAILED = 'chargeFailed',
  TRANSFERRING = 'transferring',
  TRANSFER_FAILED = 'transferFailed',
  PAID = 'paid',
  PAID_MANUALLY = 'paidManually',
  PAYOUT_REVERSED = 'payoutReversed',
  PAYOUT_REVERSED_FAILED = 'payoutReversedFailed',
  VOID = 'void',
  UNCOLLECTIBLE = 'uncollectible',
  REFUNDED = 'refunded',
  REFUND_FAILED = 'refundFailed',
  REFUND_STARTED = 'refundStarted',
}

export enum USER_STATUS_DESCRIPTION {
  PAID_MANUALLY = 'userStatusDescription.paidManually',
  REFUNDED = 'userStatusDescription.refunded',
  REFUND_FAILED = 'userStatusDescription.refundFailed',
  REFUND_STARTED = 'userStatusDescription.refundStarted',
  TRANSFER_FAILED = 'userStatusDescription.transferFailed',
}

const userInvoiceStatusMap: { [key: string]: { text: string; color?: ColorString } } = {
  [USER_STATUS.SCHEDULED]: {
    text: 'status.scheduled',
    color: 'grey',
  },
  [USER_STATUS.INVOICE_SENT]: {
    text: 'status.invoiceSent',
    color: 'blue',
  },
  [USER_STATUS.NOTIFICATION_SENT]: {
    text: 'status.notificationSent',
    color: 'blue',
  },
  [USER_STATUS.PAST_DUE]: {
    text: 'status.pastDue',
    color: 'red',
  },
  [USER_STATUS.PROCESSING]: {
    text: 'status.processing',
    color: 'yellow',
  },
  [USER_STATUS.CHARGE_FAILED]: {
    text: 'status.chargeFailed',
    color: 'red',
  },
  [USER_STATUS.TRANSFERRING]: {
    text: 'status.transferring',
    color: 'blue',
  },
  [USER_STATUS.TRANSFER_FAILED]: {
    text: 'status.transferFailed',
    color: 'red',
  },
  [USER_STATUS.PAYOUT_REVERSED]: {
    text: 'status.payoutReversed',
    color: 'red',
  },
  [USER_STATUS.PAYOUT_REVERSED_FAILED]: {
    text: 'status.payoutReversedFailed',
    color: 'red',
  },
  [USER_STATUS.PAID]: {
    text: 'status.deposited',
    color: 'green',
  },
  [USER_STATUS.PAID_MANUALLY]: {
    text: 'status.paidManually',
    color: 'green',
  },
  [USER_STATUS.VOID]: {
    text: 'status.void',
    color: 'red',
  },
  [USER_STATUS.REFUNDED]: {
    text: 'status.refunded',
    color: 'orange',
  },
  [USER_STATUS.REFUND_FAILED]: {
    text: 'status.refundFailed',
    color: 'red',
  },
  [USER_STATUS.REFUND_STARTED]: {
    text: 'status.refundStarted',
    color: 'orange',
  },
};

export const getFailurePopupText = (t: TFunction, failureMessage?: string, nextPaymentAttempt?: string) => {
  if (!failureMessage && !nextPaymentAttempt) return t('payments.pleaseContactWS');

  const nextAttemptString = nextPaymentAttempt
    ? t('payments.nextAttemptAt', { date: moment(nextPaymentAttempt).format(DefaultDateFormat) })
    : '';

  const failureMessageString = failureMessage ? t('payments.chargeFailedMessage', { message: failureMessage }) : '';

  return `${failureMessageString} ${nextAttemptString}`.trim();
};

export type UserStatusFormatter = {
  text: string;
  color?: ColorString;
  popupText: string;
  userStatus: string;
};

export type UserStatusFormatterProps = Partial<
  Pick<
    InvoiceV2,
    'userStatus' | 'userStatusDescription' | 'failureMessage' | 'nextPaymentAttempt' | 'payments' | 'refundFailedReason'
  >
>;

/**
 * This userStatus formatter will eventually replace the rest of the invoiceStatusFormatter
 * as userStatus gets implemented.
 */
export const userStatusFormatter = (
  {
    userStatus,
    userStatusDescription,
    failureMessage,
    nextPaymentAttempt,
    payments,
    refundFailedReason,
  }: UserStatusFormatterProps,
  t: TFunction
): UserStatusFormatter => {
  const format = { ...userInvoiceStatusMap[userStatus] };

  // if user status is deposited but payment hasn't settled yet, return quick deposited status
  if (
    userStatus === USER_STATUS.PAID &&
    payments &&
    payments.length > 0 &&
    !payments.some((payment) => payment.status === 'paid')
  ) {
    format.text = 'status.quickDeposited';
    format.color = 'green';
    userStatusDescription = 'userStatusDescription.quickDeposited';
  }

  if (userStatus === USER_STATUS.CHARGE_FAILED) {
    return {
      ...format,
      popupText: getFailurePopupText(t, failureMessage, nextPaymentAttempt),
      userStatus,
    };
  }

  if (userStatus === USER_STATUS.REFUND_FAILED && refundFailedReason) {
    return {
      ...format,
      popupText: t('userStatusDescription.refundFailed', { reason: refundFailedReason }),
      userStatus,
    };
  }

  if (userStatus === USER_STATUS.TRANSFER_FAILED && failureMessage) {
    return {
      ...format,
      popupText: t('userStatusDescription.transferFailedWithReason', { reason: failureMessage }),
      userStatus,
    };
  }

  return {
    ...format,
    popupText: userStatusDescription,
    userStatus,
  };
};
